import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import "./DashboardMain.css";
import MainDashboard from "../components/MainDashboard/MainDashboard";
import Clients from "../pages/Clients";
import Recipients from "../pages/Recipients";
import Aggregation from "../pages/Aggregation";
import Scanning from "../pages/Scanning";
import Awb from "../pages/Awb";
import Outgoing from "../pages/Outgoing";
import AddParcel from "../pages/AddParcel/AddParcel";
import EditParcel from "../pages/EditParcel/EditParcel";
import AddClient from "../pages/AddClien/AddClient";
import { get, BASE_URL } from "../../api/api";
import useDebounce from "../../hooks/useDebounce";

const INITIAL_FILTERS = {
  trackNumber: "",
  client: "",
  clientByWarehouse: "",
  magazine: "",
  reys: "",
  warehouse: "",
  dateCreatedStart: "",
  dateCreatedEnd: "",
  dateOnWarehouseStart: "",
  dateOnWarehouseEnd: "",
  clientRequest: "",
  status: "",
};

export default function DashboardMain() {
  const PAGE_SIZE = 20;
  const navigate = useNavigate();

  const [parcels, setParcels] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const debouncedFilters = useDebounce(filters, 500);
  const [selectedParcelId, setSelectedParcelId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [activeQueryString, setActiveQueryString] = useState("");
  const [error, setError] = useState(null);
  const loadMoreRef = useRef(null);

  const statusColors = {
    Проверяется: "#FFB74D",
    "На складе": "#4CAF50",
    Отправлена: "#2196F3",
    "На обработке": "#9CCC65",
    Прибыла: "#7E57C2",
    "Доставлена заказчику": "#66BB6A",
    "Неправильный трекинг номер": "#EF5350",
    "Возвращена отправителю": "#EC407A",
    "Задержана на складе": "#FF7043",
    Отменена: "#E53935",
  };

  const buildSearchQueryString = useCallback((searchFilters) => {
    const queryParams = new URLSearchParams();

    if (
      Array.isArray(searchFilters.trackNumber) &&
      searchFilters.trackNumber.length > 0
    ) {
      queryParams.append("tracking_number", searchFilters.trackNumber.join(","));
    } else {
      Object.entries(searchFilters).forEach(([key, value]) => {
        if (value && key !== "trackNumber") {
          switch (key) {
            case "dateCreatedStart":
              queryParams.append("created_at_after", value);
              break;
            case "dateCreatedEnd":
              queryParams.append("created_at_before", value);
              break;
            case "dateOnWarehouseStart":
              queryParams.append("date_on_warehouse_after", value);
              break;
            case "dateOnWarehouseEnd":
              queryParams.append("date_on_warehouse_before", value);
              break;
            case "client":
              queryParams.append("client", value);
              break;
            case "clientByWarehouse":
              queryParams.append("client_by_warehouse", value);
              break;
            case "magazine":
              queryParams.append("warehouse", value);
              break;
            case "reys":
              {
                const [reysYear, reysNumber] = String(value)
                  .split("/")
                  .map((part) => part.trim());

                if (reysYear) {
                  queryParams.append("reys_year", reysYear);
                }
                if (reysNumber) {
                  queryParams.append("reys_number", reysNumber);
                }
              }
              break;
            case "status":
              queryParams.append("status", value);
              break;
            case "clientRequest":
              queryParams.append("client_request", value);
              break;
            default:
              queryParams.append(key, value);
          }
        }
      });
    }

    queryParams.set("page_size", String(PAGE_SIZE));
    return queryParams.toString();
  }, []);

  const fetchParcels = useCallback(
    async ({ append = false, url = null, queryString = null } = {}) => {
      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const token = localStorage.getItem("token");
        let requestUrl = url || `${BASE_URL}/packages/`;

        if (!url && queryString) {
          requestUrl += `?${queryString}`;
        } else if (!url && !queryString) {
          requestUrl += `?page_size=${PAGE_SIZE}`;
        }

        const response = await axios.get(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const responseData = response.data;
        const paginatedData = Array.isArray(responseData)
          ? { results: responseData, next: null }
          : responseData;

        const newParcels = paginatedData.results || [];

        setParcels((prevParcels) =>
          append ? [...prevParcels, ...newParcels] : newParcels
        );
        setNextPageUrl(paginatedData.next || null);
        setHasMore(Boolean(paginatedData.next));
      } catch (error) {
        setError("Ошибка при загрузке посылок: " + error.message);
        console.error("Error fetching parcels:", error);
        if (!append) {
          setParcels([]);
          setHasMore(false);
        }
      } finally {
        if (append) {
          setIsLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    fetchParcels({ queryString: `page_size=${PAGE_SIZE}` });
    fetchStatuses();
  }, [fetchParcels]);

  useEffect(() => {
    // Запускаем поиск только если трек-номер не заполнен
    if (!filters.trackNumber.trim()) {
      handleSearch();
    }
  }, [debouncedFilters]);

  const fetchStatuses = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await get.getStatus(config);
      const formattedStatuses = response.map(([value, label]) => ({
        id: value,
        label: label,
        color: statusColors[label] || "#808080",
      }));
      setStatusOptions(formattedStatuses);
    } catch (error) {
      setError("Ошибка при загрузке статусов: " + error.message);
      console.error("Error fetching statuses:", error);
    }
  };

  const handleSearch = useCallback(
    async (searchFilters = filters) => {
      const queryString = buildSearchQueryString(searchFilters);
      setActiveQueryString(queryString);
      await fetchParcels({ queryString });
    },
    [buildSearchQueryString, fetchParcels, filters]
  );

  const fetchNextPage = useCallback(async () => {
    if (!nextPageUrl || isLoadingMore || loading) {
      return;
    }
    await fetchParcels({ append: true, url: nextPageUrl });
  }, [fetchParcels, isLoadingMore, loading, nextPageUrl]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "300px", threshold: 0 }
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
      observer.disconnect();
    };
  }, [fetchNextPage, hasMore]);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleResetFilters = useCallback(async () => {
    setFilters(INITIAL_FILTERS);
    setActiveQueryString(`page_size=${PAGE_SIZE}`);
    await fetchParcels({ queryString: `page_size=${PAGE_SIZE}` });
  }, [fetchParcels]);

  const handleStatusClick = useCallback(
    (parcelId) => {
      setSelectedParcelId(selectedParcelId === parcelId ? null : parcelId);
    },
    [selectedParcelId]
  );

  const handleStatusChange = useCallback(async (parcelId, newStatus) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      // Update the local state immediately for instant UI feedback
      setParcels(prevParcels => 
        prevParcels.map(parcel => 
          parcel.id === parcelId 
            ? { ...parcel, status: newStatus }
            : parcel
        )
      );

      // Make the API call
      await axios.patch(
        `${BASE_URL}/packages/${parcelId}/`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Close the dropdown
      setSelectedParcelId(null);
    } catch (error) {
      // Revert the optimistic update in case of error
      setParcels(prevParcels => {
        const originalParcel = prevParcels.find(p => p.id === parcelId);
        return prevParcels.map(parcel =>
          parcel.id === parcelId
            ? { ...parcel, status: originalParcel.status }
            : parcel
        );
      });
      setError("Ошибка при обновлении статуса: " + error.message);
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async (parcelId) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      await axios.delete(`${BASE_URL}/packages/${parcelId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await fetchParcels({ queryString: activeQueryString || `page_size=${PAGE_SIZE}` });
    } catch (error) {
      setError("Ошибка при удалении посылки: " + error.message);
      console.error("Error deleting parcel:", error);
    } finally {
      setLoading(false);
    }
  }, [activeQueryString, fetchParcels]);

  const handleBulkStatusChange = useCallback(async (selectedIds, newStatus) => {
    // Store original states for rollback
    const originalParcels = parcels.map(p => ({...p}));
    
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      // Optimistic update
      setParcels(prevParcels => 
        prevParcels.map(parcel => 
          selectedIds.includes(parcel.id)
            ? { ...parcel, status: newStatus }
            : parcel
        )
      );

      // Make the API calls
      await Promise.all(
        selectedIds.map((id) =>
          axios.patch(
            `${BASE_URL}/packages/${id}/`,
            { status: newStatus },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        )
      );

      setSelectedParcelId(null);
    } catch (error) {
      // Revert to original state in case of error
      setParcels(originalParcels);
      setError("Ошибка при массовом обновлении статуса: " + error.message);
      console.error("Error bulk updating status:", error);
    } finally {
      setLoading(false);
    }
  }, [parcels]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`${BASE_URL}/packages/${id}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        )
      );

      await fetchParcels({ queryString: activeQueryString || `page_size=${PAGE_SIZE}` });
    } catch (error) {
      setError("Ошибка при массовом удалении: " + error.message);
      console.error("Error bulk deleting:", error);
    } finally {
      setLoading(false);
    }
  }, [activeQueryString, fetchParcels]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <MainDashboard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleSearch={handleSearch}
            handleResetFilters={handleResetFilters}
            navigate={navigate}
            loading={loading}
            error={error}
            parcels={parcels}
            statusOptions={statusOptions}
            selectedParcelId={selectedParcelId}
            handleStatusClick={handleStatusClick}
            handleStatusChange={handleStatusChange}
            handleDelete={handleDelete}
            handleBulkStatusChange={handleBulkStatusChange}
            handleBulkDelete={handleBulkDelete}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            loadMoreRef={loadMoreRef}
          />
        }
      />
      <Route
        path="/parcels"
        element={
          <MainDashboard
            filters={filters}
            handleFilterChange={handleFilterChange}
            handleSearch={handleSearch}
            handleResetFilters={handleResetFilters}
            navigate={navigate}
            loading={loading}
            error={error}
            parcels={parcels}
            statusOptions={statusOptions}
            selectedParcelId={selectedParcelId}
            handleStatusClick={handleStatusClick}
            handleStatusChange={handleStatusChange}
            handleDelete={handleDelete}
            handleBulkStatusChange={handleBulkStatusChange}
            handleBulkDelete={handleBulkDelete}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            loadMoreRef={loadMoreRef}
          />
        }
      />
      <Route path="/clients" element={<Clients />} />
      <Route path="/clients/add/:id" element={<AddClient />} />
      <Route path="/recipients" element={<Recipients />} />
      <Route path="/aggregation" element={<Aggregation />} />
      <Route path="/scanning" element={<Scanning />} />
      <Route path="/scanning/outgoing" element={<Scanning />} />
      <Route path="/scanning/locations" element={<Scanning />} />
      <Route path="/scanning/search" element={<Scanning />} />
      <Route path="/outgoing" element={<Outgoing />} />
      <Route path="/awb" element={<Awb />} />
      <Route path="/add-parcel" element={<AddParcel />} />
      <Route path="/edit-parcel/:id" element={<EditParcel />} />
    </Routes>
  );
}
